import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Text,
  TabList,
  Tab,
  FluentProvider,
  makeStyles,
  tokens,
  shorthands,
} from '@fluentui/react-components';
import { appTheme } from '../common/appTheme';
import {
  DocumentText20Regular,
  Tag20Regular,
  Shield20Regular,
} from '@fluentui/react-icons';
import { useAppDispatch, useAppSelector } from '../hooks';
import { goDashboard, goTenant, setSelectedTenantNames } from '../slices/navSlice';
import { fetchTenants, selectTenantOptions, selectTenantsStatus } from '../slices/tenantsSlice';
import { selectDashboardKPIBlocks } from '../selectors/adminkpiSelectors';
import {
  fetchDashboardSits,
  fetchDashboardPolicies,
  clearAllEntities
} from '../slices/dashboardEntitySlice';
import { fetchTenantCounts, selectTenantCounts } from '../slices/tenantCountsSlice';
import { selectTenantIdsByNames } from '../slices/tenantsSlice';
import { selectDashboardPoliciesMetadata } from '../slices/dashboardPoliciesSlice';

import { SITTable } from './DashboardSITTable';
import LabelsTable from './DashboardLabelsTable';
import PoliciesTable from './policies/DashboardPoliciesTable';
import TenantDashboard from './TenantDashboard';
import ViewSwitch from './common/ViewSwitch';
import { arraysEqual } from './common/utils';
import { BASE_TEMPLATE_TENANT, DASHBOARD_HEADING, getPageTitle } from '../common/constants';
import { selectTenantNameById } from '../slices/tenantsSlice';
import { DashboardLayoutStyles } from './Styles';
const TAB_IDS = { sit: 'tab-sit', labels: 'tab-labels', policies: 'tab-policies' } as const;

export default function DashboardLayout() {
  const s = DashboardLayoutStyles();
  const dispatch = useAppDispatch();
  const { view, tenantId, selectedDashboardTenantNames } = useAppSelector((st) => st.nav);

  // data readiness
  const tenantOptions = useAppSelector(selectTenantOptions);
  const tenantsStatus = useAppSelector(selectTenantsStatus);
  const kpi = useAppSelector(selectDashboardKPIBlocks);

  // Get tenant counts (same totals across all tenants)
  const tenantCountsData = useAppSelector(selectTenantCounts);
  const dashboardPoliciesMeta = useAppSelector(selectDashboardPoliciesMetadata);

  // Total policies for the Policies tab badge = DLP total + Auto Label total
  // (server-side totals, matching the two sub-tab counts), not the current page length.
  const totalPoliciesCount =
    (dashboardPoliciesMeta.dlpTotalCount ?? 0) + (dashboardPoliciesMeta.autoLabelTotalCount ?? 0);

  // Get any tenant's counts since totalSITs, totalLabels, totalPolicies are the same
  const anyTenantCounts = useMemo(() => {
    const tenantIds = Object.keys(tenantCountsData);
    return tenantIds.length > 0 ? tenantCountsData[tenantIds[0]] : null;
  }, [tenantCountsData]);

  useEffect(() => {
    if (tenantsStatus === 'idle' || tenantOptions.length === 0) dispatch(fetchTenants());
  }, [dispatch]);

  // Fetch tenant counts for totals (use any tenant since totals are the same)
  useEffect(() => {
    if (!anyTenantCounts && tenantOptions.length > 0) {
      const firstTenant = tenantOptions[0];
      if (firstTenant?.id) {
        dispatch(fetchTenantCounts(firstTenant.id));
      }
    }
  }, [anyTenantCounts, tenantOptions]);

  // Fix accessibility issues with Fluent UI tabster dummy elements
  useEffect(() => {
    const fixTabsterElements = () => {
      const tabsterElements = document.querySelectorAll('[data-tabster-dummy][aria-hidden="true"]');
      tabsterElements.forEach((element) => {
        if (element.getAttribute('tabindex') === '0') {
          element.setAttribute('tabindex', '-1');
        }
      });
    };

    // Fix immediately and on mutations
    fixTabsterElements();

    const observer = new MutationObserver(fixTabsterElements);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['tabindex', 'aria-hidden'],
    });

    return () => observer.disconnect();
  }, []);

  const [activeTab, setActiveTab] = useState<'sit' | 'labels' | 'policies'>('labels');
  const isDashboard = view === 'dashboard';
  const isTenant = view === 'tenant';

  // Dynamic browser tab title
  const tenantName = useAppSelector(state =>
    tenantId ? selectTenantNameById(state, tenantId) : null
  );

  useEffect(() => {
    document.title = getPageTitle(view, tenantName);
  }, [view, tenantName]);

  // Shared tenant filter state
  const allTenantNames = useMemo(
    () => tenantOptions.map((t) => t.name).sort((a, b) => a.localeCompare(b)),
    [tenantOptions],
  );

  // Use Redux state for selected tenants
  const selectedTenants = selectedDashboardTenantNames;

  // Initialize with all tenants if empty (for dashboard view)
  useEffect(() => {
    if (isDashboard && allTenantNames.length && selectedTenants.length === 0) {
      dispatch(setSelectedTenantNames(allTenantNames));
    }
  }, [isDashboard, allTenantNames, selectedTenants.length]);

  // Get tenant IDs from tenant names using selector
  const tenantIds = useAppSelector(state => selectTenantIdsByNames(state, selectedTenants));

  // Dashboard entity API triggers
  useEffect(() => {
    // Only trigger API calls in dashboard view with selected tenants
    if (isDashboard && tenantIds.length > 0) {

      // Dispatch the appropriate thunk based on active tab
      if (activeTab === 'sit') {
        dispatch(fetchDashboardSits({ tenantIds, pageNumber: 1 }));
      } else if (activeTab === 'policies') {
        dispatch(fetchDashboardPolicies({ tenantIds, pageNumber: 1 }));
      }
      // Labels tab self-fetches via labelDeploymentSlice inside DashboardLabelsTable
    }
  }, [isDashboard, activeTab, tenantIds]);

  // Clear all entities when filter changes
  const prevSelectedTenantsRef = useRef<string[]>([]);
  useEffect(() => {
    const prevTenants = prevSelectedTenantsRef.current;
    const currentTenants = selectedTenants;

    if (isDashboard && !arraysEqual(prevTenants, currentTenants) && prevTenants.length > 0) {
      dispatch(clearAllEntities());
    }

    prevSelectedTenantsRef.current = [...selectedTenants];
  }, [isDashboard, selectedTenants]);

  // Route to tenant page (both screens now share identical padding)
  if (isTenant) {
    return (
      <FluentProvider theme={appTheme}>
        <TenantDashboard />
      </FluentProvider>
    );
  }

  // Dashboard view
  return (
    <FluentProvider theme={appTheme}>
      <div className={s.pageWrap}>
        <div className={s.surface}>
          {/* Heading */}
          <div className={s.headingWrap}>
            <div className={s.headingRow}>
              <Text as="h1" className={s.headingTitle}>
                {DASHBOARD_HEADING}
              </Text>

              <div className={s.rightControls}>
                <Text className={s.baseTemplateText}>
                  <span className="base-template-label">Base template:</span>
                  <span>{BASE_TEMPLATE_TENANT}</span>
                </Text>

                <ViewSwitch
                  isTenant={isTenant}
                  isDashboard={isDashboard}
                  onTenant={() => dispatch(goTenant(tenantId || 'tenant-001'))}
                  onDashboard={() => dispatch(goDashboard())}
                />
              </div>
            </div>

            <Text as="span" className={s.headingSub}>
              Centrally manage and monitor SITs, sensitivity labels, and DLP policies across all
              tenants
            </Text>
          </div>

          {/* Tabs + toolbar */}
          <div className={s.tabsRow}>
            <TabList
              aria-label="Dashboard sections"
              selectedValue={activeTab}
              onTabSelect={(_, d) => setActiveTab(d.value as 'sit' | 'labels' | 'policies')}
              className={s.accessibleTabList}
            >
              {/* SIT tab hidden — deployment not yet supported */}
              {false && <Tab id={TAB_IDS.sit} value="sit">
                <div className={s.tabWithIcon}>
                  <DocumentText20Regular />
                  <span className={s.highContrastText}>SITs ({anyTenantCounts?.totalSITs ?? kpi.sits.total})</span>
                </div>
              </Tab>}
              <Tab id={TAB_IDS.labels} value="labels">
                <div className={s.tabWithIcon}>
                  <Tag20Regular />
                  <span className={s.highContrastText}>
                    Sensitivity Labels ({anyTenantCounts?.totalLabels ?? kpi.labels.total})
                  </span>
                </div>
              </Tab>
              <Tab id={TAB_IDS.policies} value="policies">
                <div className={s.tabWithIcon}>
                  <Shield20Regular />
                  <span className={s.highContrastText}>Policies ({totalPoliciesCount || anyTenantCounts?.totalPolicies || kpi.policies.total})</span>
                </div>
              </Tab>
            </TabList>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            </div>
          </div>

          {/* Content */}
          <section
            className={s.section}
            role="tabpanel"
            aria-labelledby={TAB_IDS[activeTab]}
            tabIndex={0}
          >
            {false && activeTab === 'sit' && (
              <SITTable allTenantNames={allTenantNames} selectedTenants={selectedTenants} />
            )}
            {activeTab === 'labels' && (
              <LabelsTable allTenantNames={allTenantNames} selectedTenants={selectedTenants} />
            )}
            {activeTab === 'policies' && (
              <PoliciesTable />
            )}
          </section>
        </div>
      </div>
    </FluentProvider>
  );
}
